// Client-side ad / analytics tags (ads conversion tracking — Phase 3).
//
// Injects gtag.js (GA4 + Google Ads) and the Meta Pixel from the tenant's
// tracking config and fires funnel + conversion events. Everything is
// CONSENT-GATED: when the tenant sets consent_required, no tag loads or fires
// until the guest grants marketing consent at checkout. Best-effort — a missing
// global or a blocked script never throws into the booking flow.
//
// Purchases: the browser sends a platform's purchase only where the booking
// has no server twin, and every path claims ONE conversion per booking
// (claimConversion), shared across paths. The SERVER (#1119, 20260820000040)
// reports to GA4 (Measurement Protocol) and Meta (CAPI) every paid booking
// from its payment at the amount charged, and every other native booking as
// it goes pending → confirmed (the booking's amount, else the service's
// estimated value, else 0), with the booking reference as the dedup key. So:
//   - GA4 purchase: only for a spa booking confirmed without a deposit (its
//     board row is inserted confirmed, so neither server rail fires);
//   - Meta Purchase: that spa booking, and the browser leg of a paid
//     booking's pair on the Paystack return (same eventID, same amount);
//   - Google Ads conversion: on every confirmed path — the server's Ads
//     upload is not configured, so the browser is the only Ads path.
// Leads, the same way: the server's lead rail sends GA4 generate_lead and the
// Meta Lead for every enquiry, so the browser sends only the Meta Lead (the
// pair's browser leg, eventID = enquiry id). begin_checkout has no server
// twin: the browser's.
//
// Embedded in a venue's own page (lib/embed-context.ts), that page may run
// tags of its own: the venue's then own the page view (injectGoogle,
// injectMeta), and Vertu's events go only to the IDs the venue configured in
// Vertu (googleEvent, metaEvent). On the hosted booking page ours are the only
// tags. The venue's own GTM hears of bookings through its dataLayer instead
// (state/host-datalayer.ts).

import { consentCommands, hostConsentGranted } from "../lib/consent-mode";
import { isEmbedded } from "../lib/embed-context";
import type { WidgetTracking } from "../types";
import { claimOnce, sessionStore } from "./host-datalayer";

const CONSENT_KEY = "vt-consent-marketing";

// Consent is PER TENANT — a guest who opted into marketing at venue A has
// said nothing about venue B, and the bare key on the shared hosted origin
// silently satisfied every venue's gate at once. POPIA-relevant, not
// cosmetic. No legacy migration on purpose: a bare consent cannot say which
// venue it was granted to, so it counts for none of them.
let consentScope = "";
export function scopeMarketingConsent(tenantSlug: string): void {
  if (tenantSlug) consentScope = tenantSlug;
}
function consentKey(): string | null {
  return consentScope ? `${CONSENT_KEY}:${consentScope}` : null;
}

type Gtag = (...args: unknown[]) => void;
interface Fbq {
  (...args: unknown[]): void;
  queue?: unknown[];
  callMethod?: (...args: unknown[]) => void;
  loaded?: boolean;
  version?: string;
  push?: Fbq;
  /** fbevents.js, once loaded: the pixels this page initialised. */
  getState?: () => { pixels?: Array<{ id?: unknown }> } | undefined;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    /** Vertu's own gtag.js instance's data layer (injectGoogle). */
    vertuDataLayer?: unknown[];
    gtag?: Gtag;
    fbq?: Fbq;
    _fbq?: Fbq;
    /** Set by Google Tag Manager's gtm.js once a container runs. */
    google_tag_manager?: unknown;
  }
}

let config: WidgetTracking | null = null;
let injected = false;

/** Has the guest granted marketing consent (persisted at checkout)? */
export function hasMarketingConsent(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const k = consentKey();
    // Fail closed: without a tenant scope there is no one the consent could
    // belong to.
    return k != null && window.localStorage.getItem(k) === "1";
  } catch {
    return false;
  }
}

/** Persist the guest's marketing-consent choice and (if granted) load tags now. */
export function setMarketingConsent(granted: boolean): void {
  if (typeof window === "undefined") return;
  try {
    const k = consentKey();
    if (k != null) window.localStorage.setItem(k, granted ? "1" : "0");
  } catch {
    /* ignore */
  }
  if (granted) ensureTagsInjected();
}

// A grant INHERITED from the tenant's own site (tag.js appends ?vt_consent=1
// when a CMP there already holds marketing consent), as distinct from a choice
// this guest made in a Vertu checkout. Deliberately NOT stored under
// CONSENT_KEY: the checkout may pre-tick an inherited grant, but must never
// pre-tick merely because this device opted in to some booking last month.
// sessionStorage, so it lasts exactly the visit the site tag vouched for.
const INHERITED_KEY = "vt-consent-inherited";

/** Record that the tenant's own site vouched for this visit's consent. */
export function setConsentInherited(): void {
  if (typeof window === "undefined" || !consentScope) return;
  try {
    window.sessionStorage.setItem(`${INHERITED_KEY}:${consentScope}`, "1");
  } catch {
    /* Unavailable in some embeds — the checkout then simply asks, which is
       the safe direction to fail. */
  }
}

/**
 * Did consent for this visit arrive inherited rather than freshly given?
 *
 * Embedded in the venue's own page, the page's own cookie banner counts too:
 * its Google Consent Mode state in the dataLayer (ad_storage or
 * analytics_storage granted), or data-consent="granted" on the widget's mount
 * node (lib/consent-mode.ts). Read each time this is asked — the checkout
 * asks as it opens — never once at mount: a banner answers after the page
 * loaded. It only PRE-TICKS the marketing box, which the guest still sees and
 * can untick. Unlike ?vt_consent=1 (main.tsx), the page's grant never counts
 * as consent for Vertu's own tags (hasMarketingConsent): the box, as the guest
 * leaves it at checkout, decides that.
 */
export function consentWasInherited(): boolean {
  if (typeof window === "undefined" || !consentScope) return false;
  if (isEmbedded() && hostConsentGranted(consentScope)) return true;
  try {
    return (
      window.sessionStorage.getItem(`${INHERITED_KEY}:${consentScope}`) === "1"
    );
  } catch {
    return false;
  }
}

function canTrack(): boolean {
  if (!config) return false;
  if (config.consent_required && !hasMarketingConsent()) return false;
  return true;
}

/** Called once when the widget config is ready. */
export function initTracking(tracking: WidgetTracking | null | undefined): void {
  // gtag/Pixel are page-global, but the widget can mount several embeds per
  // page. Once a tenant's tags have loaded, ignore later (re)inits so we never
  // overwrite the active config and end up firing one tenant's loaded scripts
  // against another tenant's IDs — first configured tenant wins.
  if (injected) return;
  config = tracking ?? null;
  ensureTagsInjected();
}

function ensureTagsInjected(): void {
  if (injected || typeof document === "undefined" || !canTrack() || !config) return;
  const hasGoogle = Boolean(
    config.ga4_measurement_id || config.google_ads_conversion_id,
  );
  const hasMeta = Boolean(config.meta_pixel_id);
  if (!hasGoogle && !hasMeta) return;
  injected = true;
  if (hasGoogle) injectGoogle(config.ga4_measurement_id, config.google_ads_conversion_id);
  if (hasMeta && config.meta_pixel_id) injectMeta(config.meta_pixel_id);
}

// ─── The host page's own tags ──────────────────────────────────────────────
// Rule: when the page already runs a platform's tag, the page owns its page
// views for that platform. Ours would count the same page a second time.

/**
 * Does the page run a Google tag of its own, before ours loads? Tag Manager's
 * runtime (google_tag_manager, which gtag.js sets too), Tag Manager's snippet
 * in the dataLayer (its gtm.js start: the runtime can still be loading), a
 * gtag snippet that configures a tag (a "config" command), or the script of
 * either on the page. A gtag function alone is not one: a cookie banner's
 * Consent Mode stub defines it on pages that run no Google tag at all.
 */
export function hostRunsGoogleTags(
  page: { google_tag_manager?: unknown; dataLayer?: unknown },
  doc?: { querySelector?: (selectors: string) => unknown } | null,
): boolean {
  if (page.google_tag_manager != null) return true;
  const dl = Array.isArray(page.dataLayer) ? page.dataLayer : [];
  for (const entry of dl) {
    if (entry == null || typeof entry !== "object") continue;
    if ((entry as { event?: unknown }).event === "gtm.js") return true;
    if ((entry as { [i: number]: unknown })[0] === "config") return true;
  }
  try {
    return Boolean(
      doc?.querySelector?.(
        'script[src*="googletagmanager.com/gtag/js"], script[src*="googletagmanager.com/gtm.js"]',
      ),
    );
  } catch {
    return false;
  }
}

/**
 * Has this pixel been initialised on the page already (fbevents.js's
 * getState)? Unknown — the stub before fbevents.js loads has no getState —
 * reads as no: a second init of one pixel is harmless, Meta ignores it.
 */
export function pixelInitialised(fbq: Pick<Fbq, "getState"> | undefined, pixelId: string): boolean {
  try {
    const pixels = fbq?.getState?.()?.pixels;
    return Array.isArray(pixels) && pixels.some((p) => p != null && String(p.id) === pixelId);
  } catch {
    return false;
  }
}

// ─── Vertu's own Google tag ────────────────────────────────────────────────
// Its own gtag.js instance on its OWN data layer (gtag/js?…&l=vertuDataLayer),
// on the hosted page and on embeds alike. A venue page's Google tags read
// window.dataLayer and Vertu's reads only window.vertuDataLayer, so the
// venue's own events never reach Vertu's IDs, Vertu's calls (the guest's
// email for Enhanced Conversions among them) never enter the venue's
// dataLayer, and Vertu's instance starts itself ("js") on any page.
const VERTU_DATA_LAYER = "vertuDataLayer";

// Google's gtag snippet, on Vertu's data layer: a real function that pushes
// its `arguments` object, which is all gtag.js acts on. The arrays the widget
// pushed before 14 Oct 2026 (`(...args) => dataLayer.push(args)`) reached no
// Google tag, so Vertu's GA4 and Google Ads calls never left the page.
const vertuGtag: Gtag = function gtag() {
  // eslint-disable-next-line prefer-rest-params
  (window.vertuDataLayer = window.vertuDataLayer || []).push(arguments);
};

// Set once Vertu's Google tag is on the page (injectGoogle).
let gtagFn: Gtag | null = null;

// Consent Mode is the page's: once any Google tag has read the banner's
// consent commands, Google holds that state for every tag on the page,
// Vertu's included. A page whose banner left only its gtag stub has no tag to
// read them, though, and Vertu's tag reads only its own data layer. So the
// banner's consent commands (lib/consent-mode.ts consentCommands) are copied
// into Vertu's data layer in order: those already there before Vertu's tag
// starts, then (checked every CONSENT_MIRROR_MS, and before each of Vertu's
// events) any later ones. Where a Google tag of the page's own has read them
// too, the copy changes nothing. Wherever Vertu's tag loads, not only on an
// embed: the hosted page has no window.dataLayer (nothing to copy), and an
// embed taken for the hosted page (no currentScript) keeps its banner's word.
const CONSENT_MIRROR_MS = 1000;
let consentCopied = 0;

function mirrorHostConsent(): void {
  try {
    const dl = window.dataLayer;
    if (!Array.isArray(dl)) return;
    if (dl.length < consentCopied) consentCopied = 0;
    for (const args of consentCommands(dl, consentCopied)) vertuGtag(...args);
    consentCopied = dl.length;
  } catch {
    /* never throw into the booking flow */
  }
}

function injectGoogle(ga4: string | null, adsId: string | null): void {
  const primary = ga4 || adsId;
  if (!primary) return;
  // Read before anything of ours lands on the page.
  const hostTags = hostRunsGoogleTags(window, document);
  mirrorHostConsent();
  window.setInterval(mirrorHostConsent, CONSENT_MIRROR_MS);
  gtagFn = vertuGtag;
  vertuGtag("js", new Date());
  // The venue's Google tags own this page's page_view: ours are configured
  // without one of their own. On the hosted page (and an embed on a page
  // with no Google tag) they send it, as Google's snippet does.
  const settings: unknown[] = hostTags ? [{ send_page_view: false }] : [];
  if (ga4) vertuGtag("config", ga4, ...settings);
  if (adsId) vertuGtag("config", adsId, ...settings);
  const s = document.createElement("script");
  s.async = true;
  s.src =
    "https://www.googletagmanager.com/gtag/js?id=" +
    encodeURIComponent(primary) +
    "&l=" +
    VERTU_DATA_LAYER;
  document.head.appendChild(s);
}

function injectMeta(pixelId: string): void {
  // The page's own pixel was here first: it owns the PageView.
  const hostPixel = Boolean(window.fbq);
  if (!window.fbq) {
    const fbq = function (...args: unknown[]) {
      if (fbq.callMethod) fbq.callMethod.apply(fbq, args);
      else (fbq.queue as unknown[]).push(args);
    } as Fbq;
    fbq.queue = [];
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.push = fbq;
    window.fbq = fbq;
    if (!window._fbq) window._fbq = fbq;
    const t = document.createElement("script");
    t.async = true;
    t.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(t);
  }
  if (!pixelInitialised(window.fbq, pixelId)) window.fbq("init", pixelId);
  if (!hostPixel) window.fbq("track", "PageView");
}

// ─── Where Vertu's own events go ───────────────────────────────────────────
// Embedded in a venue's page, the page may run tags of its own (or start to,
// after ours): an event sent to "every tag on the page" would reach the
// venue's tags too. So there they go only to the IDs the venue configured in
// Vertu — the tags that always got them. On the hosted booking page ours are
// the only tags, and the calls are made as they always were.

function vertuGoogleIds(): string[] {
  return [config?.ga4_measurement_id, config?.google_ads_conversion_id].filter(
    (id): id is string => Boolean(id),
  );
}

function googleEvent(name: string, params: Record<string, unknown>): void {
  if (!gtagFn) return;
  mirrorHostConsent();
  gtagFn("event", name, isEmbedded() ? { ...params, send_to: vertuGoogleIds() } : params);
}

/** `args`: the event's parameters, then (optionally) its options ({eventID}). */
function metaEvent(name: string, ...args: unknown[]): void {
  const fbq = window.fbq;
  if (!fbq) return;
  if (!isEmbedded()) {
    fbq("track", name, ...args);
    return;
  }
  // trackSingle: the pixel the venue configured in Vertu, never every pixel
  // the page runs. No pixel configured in Vertu, no Meta event from Vertu.
  const pixelId = config?.meta_pixel_id;
  if (pixelId) fbq("trackSingle", pixelId, name, ...args);
}

// ─── Events (values in major currency units, e.g. rands) ──────────────────
export function trackBeginCheckout(value: number, currency: string): void {
  if (!canTrack()) return;
  ensureTagsInjected();
  googleEvent("begin_checkout", { value, currency });
  metaEvent("InitiateCheckout", { value, currency });
}

/**
 * A lead (an enquiry sent). The Meta Lead only: no GA4 generate_lead from the
 * browser, on the same rule as the purchase. The server's lead rail sends one
 * per enquiry over the Measurement Protocol (trg_enqueue_lead_conversions,
 * once the enquiry's attribution is written), and GA4 deduplicates
 * transaction_id for purchase alone, so a browser generate_lead beside it
 * would count every enquiry twice.
 */
export function trackLead(opts: {
  value?: number;
  currency: string;
  /**
   * Stable dedup id for this lead — the enquiry id. Passed as the Meta
   * eventID so the server's lead CAPI event (dedup_key = the same enquiry
   * id) dedupes against this browser event.
   */
  eventId?: string;
}): void {
  if (!canTrack()) return;
  ensureTagsInjected();
  metaEvent(
    "Lead",
    { value: opts.value, currency: opts.currency },
    opts.eventId ? { eventID: opts.eventId } : undefined,
  );
}

// One conversion per booking, whichever path sees it (the in-widget
// confirmation, the Paystack return, a checkout that saw its earlier payment
// go through, the spa deposit's return): in this page and, through
// sessionStorage, this visit — a booking the watch confirmed and the return
// address then showed again converts once.
const CONVERSION_KEY = "vt-ad-conversion";
const firedConversions = new Set<string>();

function claimConversion(txn: string | undefined): boolean {
  return !txn || claimOnce(`${CONVERSION_KEY}:${consentScope}:${txn}`, firedConversions, sessionStore());
}

/**
 * A booking the widget confirmed itself (no Paystack: a free cart, a spa
 * booking without a deposit). The Google Ads conversion, always. GA4 and Meta
 * only where the server has no twin of it (`serverReports: false`, the spa
 * booking): there the browser is its only purchase. A free native booking
 * reaches GA4 and Meta from the server at the service's estimated value, and
 * a browser R0 beside it would be the same booking at another value.
 */
export function trackPurchase(opts: {
  reference: string | null;
  value: number;
  currency: string;
  email?: string | null;
  /** Does the server report this booking to GA4 and Meta itself? */
  serverReports: boolean;
}): void {
  if (!canTrack()) return;
  ensureTagsInjected();
  const txn = opts.reference ?? undefined;
  if (!claimConversion(txn)) return;
  fireGoogleAdsConversion(opts.value, opts.currency, txn, opts.email);
  if (!opts.serverReports)
    googleEvent("purchase", { transaction_id: txn, value: opts.value, currency: opts.currency });
  // eventID = booking reference, the server CAPI event's dedup key.
  if (!opts.serverReports || opts.value > 0)
    metaEvent(
      "Purchase",
      { value: opts.value, currency: opts.currency },
      txn ? { eventID: txn } : undefined,
    );
}

/**
 * Only the Google Ads conversion, at the amount paid: a paid booking the
 * server reports to GA4 and Meta from its payment, confirmed on a path
 * without the Paystack return's Meta leg — the spa deposit's return, a
 * checkout that saw its earlier payment go through. The browser is the only
 * Ads path; the claim is the one every path shares, so a booking seen on two
 * paths (the watch, then the return address) converts once.
 */
export function trackAdsConversion(opts: {
  reference: string | null;
  value: number;
  currency: string;
  email?: string | null;
}): void {
  if (!canTrack()) return;
  ensureTagsInjected();
  const txn = opts.reference ?? undefined;
  if (!claimConversion(txn)) return;
  fireGoogleAdsConversion(opts.value, opts.currency, txn, opts.email);
}

/**
 * The Paystack return: a paid booking, whose GA4 and Meta purchase the server
 * reports from its payment. The Google Ads conversion (the browser is the only
 * Ads path) and the browser's Meta leg of the server's Purchase.
 */
export function trackGoogleAdsConversion(opts: {
  reference: string | null;
  value: number;
  currency: string;
  email?: string | null;
}): void {
  if (!canTrack()) return;
  ensureTagsInjected();
  const txn = opts.reference ?? undefined;
  if (!claimConversion(txn)) return;
  fireGoogleAdsConversion(opts.value, opts.currency, txn, opts.email);
  // Meta Purchase for the PAID (Paystack) leg too. The server CAPI event for
  // this booking carries the same reference as its event_id and the amount
  // charged as its value, so Meta deduplicates the pair rather than
  // double-counting — and dedup NEEDS the pair: with the browser silent
  // here, Events Manager scored the dataset's event-ID coverage at 0% and
  // match quality suffered, because the browser leg is what carries
  // fbp/fbc/IP/UA. Redundant send + event_id dedup is Meta's own recommended
  // architecture.
  if (opts.value > 0)
    metaEvent(
      "Purchase",
      { value: opts.value, currency: opts.currency },
      txn ? { eventID: txn } : undefined,
    );
}

// Google Ads conversion event, fired only when an id + label are configured.
// When an email is supplied we set first-party user_data for Enhanced
// Conversions — gtag hashes it client-side before sending, recovering
// conversions that cookies/ad-blockers miss. (Takes effect once the tenant
// enables Enhanced Conversions in their Google Ads account.) It stays on
// Vertu's own data layer: never the venue's.
function fireGoogleAdsConversion(
  value: number,
  currency: string,
  txn: string | undefined,
  email?: string | null,
): void {
  if (!gtagFn || !config?.google_ads_conversion_id || !config.google_ads_conversion_label) return;
  mirrorHostConsent();
  if (email) gtagFn("set", "user_data", { email });
  // Already addressed to Vertu's conversion alone (send_to), embedded or not.
  const params: Record<string, unknown> = {
    send_to: `${config.google_ads_conversion_id}/${config.google_ads_conversion_label}`,
    transaction_id: txn,
  };
  // A free booking carries no value at all, so Google Ads counts the
  // conversion action's own default value for it rather than R0.
  if (value > 0) {
    params.value = value;
    params.currency = currency;
  }
  gtagFn("event", "conversion", params);
}
